import ERP from "@apiV2/ERP.mjs";
import Schema from "@apiV2/Schema.mjs";
import View from "@apiV2/View.mjs";
import useUsuarioStore from "@/api/v2/stores/UsuarioStore";
import {
  toIsoDay, hojeIso, diasEntre, textoDe, idDe, unicos, getManyByIdEmLotes, resumirContasAPagar
} from "@/views/Relatorios/services/relatorioGeralUtils";

// Relatório Geral de Ordem de Pagamento (Financeiro).
// Fonte: view "ordem-de-pagamento-relatorio-geral" (clone da view da tela de OP SEM o filtro de owner) -- traz despesas[] e aprovadores[]
// embutidos. A tela passa owner=usuário logado; aqui tentamos SEM owner (visão geral) e, se a
// view não responder assim, caímos para o owner do usuário (modo limitado, avisado na tela).
// CPF/CNPJ e Centro de Custo são buscados em lote (Fornecedor / Collaborator / CostCenter).
// Situação do pagamento vem da view "contas-a-pagar" (grupos tipo "Ordem de Pagamento").

const EMPRESA = "DELTA DUCON";
const TIPOS_APROVADOR = ["Requisitante", "Coordenador", "Gestor", "Financeiro"];

const lista = (resposta) => (resposta?.status === 200 && Array.isArray(resposta?.data) ? resposta.data : []);

const carregarOrdens = async () => {
  // View dedicada ao relatório (sem filtro de owner) -> traz TODAS as OPs.
  try {
    const todas = lista(await ERP.getManyWithView(View.ORDEM_DE_PAGAMENTO_RELATORIO_GERAL));
    if (todas.length) return { ordens: todas, modoLimitado: false };
  } catch (e) { /* cai para o modo limitado */ }

  const usuarioStore = useUsuarioStore();
  const comOwner = lista(await ERP.getManyWithView(View.ORDEM_DE_PAGAMENTO, [
    { name: "owner", dataType: "String", value: usuarioStore?.usuario?.id }
  ]));
  return { ordens: comOwner, modoLimitado: true };
};

const nomeCredor = (op) => {
  if (op.tipoCredor === "Colaborador") return textoDe(op.credorColaborador?.nome || op.credorColaborador);
  const f = op.credorFornecedor || {};
  return textoDe(f.nomeFantasia || f.razaoSocial || f.nome || f);
};

const grupoDaDespesa = (d) => {
  // No schema de Despesa o "grupo" é o campo tipo (ex.: "50 - TEMPORARIO - Outras Despesas").
  const g = d?.despesa?.grupo || d?.despesa?.tipo;
  if (g && typeof g === "object") return textoDe(g.descricao || g.nome || g.codigo || g);
  return g ? String(g).trim() : "";
};

const pivotarAprovadores = (aprovadores = []) => {
  const validos = aprovadores.filter((a) => a?.status !== "Cancelado");
  const pivot = {};
  TIPOS_APROVADOR.forEach((tipo) => {
    const doTipo = validos.filter((a) => a?.aprovadorTipo === tipo);
    pivot[tipo] = {
      nomes: unicos(doTipo.map((a) => textoDe(a.aprovador))).join(" / "),
      decisao: unicos(doTipo.map((a) => a.decisao)).join(" / "),
      datas: unicos(doTipo.map((a) => toIsoDay(a.dataDecisao))).join(" / ")
    };
  });
  return pivot;
};

export default {
  async listar() {
    const [{ ordens, modoLimitado }, respContas] = await Promise.all([
      carregarOrdens(),
      ERP.getManyWithView(View.CONTAS_A_PAGAR)
    ]);

    // ---- enriquecimento em lote: CPF/CNPJ + centro de custo do credor ----
    const idsFornecedor = ordens.filter((o) => o.tipoCredor !== "Colaborador").map((o) => idDe(o.credorFornecedor));
    const idsColaborador = ordens.filter((o) => o.tipoCredor === "Colaborador").map((o) => idDe(o.credorColaborador));
    const [fornecedores, colaboradores] = await Promise.all([
      getManyByIdEmLotes(Schema.FORNECEDOR, idsFornecedor).catch(() => []),
      getManyByIdEmLotes(Schema.COLABORADOR, idsColaborador).catch(() => [])
    ]);
    // Divisão do aprovador = grupo do colaborador aprovador
    const idsAprovadores = ordens.flatMap((o) => (o.aprovadores || []).map((a) => idDe(a.aprovador)));
    const aprovadoresColab = await getManyByIdEmLotes(Schema.COLABORADOR, idsAprovadores).catch(() => []);
    const mapaDivisao = {};
    aprovadoresColab.forEach((c) => { mapaDivisao[c.id] = c.grupo || ""; });
    const centrosCusto = await getManyByIdEmLotes("CostCenter", colaboradores.map((c) => c.costCenterId)).catch(() => []);

    const mapaFornecedor = {};
    fornecedores.forEach((f) => { mapaFornecedor[f.id] = f.cnpj || f.cpf || ""; });
    const mapaColaborador = {};
    colaboradores.forEach((c) => { mapaColaborador[c.id] = c; });
    const mapaCentro = {};
    centrosCusto.forEach((c) => { mapaCentro[c.id] = c; });

    // ---- situação do pagamento (Conta a Pagar vinculada) ----
    const contasPorOp = {};
    lista(respContas).filter((g) => g?.tipo === "Ordem de Pagamento").forEach((g) => { contasPorOp[g.id] = resumirContasAPagar(g); });

    const hoje = hojeIso();

    const linhas = [];
    const aprovadoresLinhas = [];

    ordens.forEach((op) => {
      const credorId = idDe(op.tipoCredor === "Colaborador" ? op.credorColaborador : op.credorFornecedor);
      const colaborador = op.tipoCredor === "Colaborador" ? mapaColaborador[credorId] : null;
      const centro = colaborador ? mapaCentro[colaborador.costCenterId] : null;
      const documento = op.tipoCredor === "Colaborador" ? colaborador?.cpf || "" : mapaFornecedor[credorId] || "";

      const conta = contasPorOp[op.id];
      const pago = !!conta?.existe && conta.soma.valorOriginal > 0 && conta.soma.valorPago >= conta.soma.valorOriginal - 0.009;
      const situacaoPagamento = !conta?.existe ? "Sem conta a pagar" : pago ? "Pago" : "Em aberto";

      const vencimento = toIsoDay(op.dataPrevisaoPagamento);
      const vencida = !!vencimento && vencimento < hoje && situacaoPagamento !== "Pago";
      const diasAtraso = vencida ? diasEntre(vencimento, hoje) : 0;

      const piv = pivotarAprovadores(op.aprovadores || []);
      const despesas = Array.isArray(op.despesas) && op.despesas.length ? op.despesas : [null];

      despesas.forEach((d, idx) => {
        linhas.push({
          _id: `${op.id || op.numero}-${idx}`,
          _opId: op.id || op.numero,
          empresa: EMPRESA,
          tipo: op.tipo || "",
          op: op.numero || "",
          dataEmissao: toIsoDay(op.dataCriacao),
          dataVencimento: vencimento,
          status: op.status || "",
          valorOp: Number(op.valorTotal || 0),
          itemDespesa: d?.numero || "",
          numeroDespesa: d?.despesa?.codigo || "",
          descricaoDespesa: d?.despesa?.descricao || "",
          grupoDespesa: grupoDaDespesa(d),
          valorDespesa: Number(d?.valor || 0),
          tipoCredor: op.tipoCredor || "",
          nomeCredor: nomeCredor(op),
          documento,
          numeroCentroCusto: textoDe(centro?.code || centro?.codigo || ""),
          centroCusto: textoDe(centro?.description || centro?.descricao || centro?.name || centro?.sigla || ""),
          job: textoDe(op.job?.codigo || ""),
          os: textoDe(op.ordemDeServico?.codigo || ""),
          emitente: textoDe(op.emitente),
          requisitante: textoDe(op.requisitante),
          observacaoDespesa: d?.observacao || "",
          justificativa: op.justificativa || "",
          situacaoPagamento,
          vencida,
          diasAtraso,
          ...TIPOS_APROVADOR.reduce((acc, tipo) => ({
            ...acc,
            [`apr${tipo}`]: piv[tipo].nomes,
            [`dec${tipo}`]: piv[tipo].decisao,
            [`dat${tipo}`]: piv[tipo].datas
          }), {})
        });
      });

      (op.aprovadores || []).forEach((a) => {
        aprovadoresLinhas.push({
          op: op.numero || "",
          statusOp: op.status || "",
          tipoAprovador: a.aprovadorTipo || "",
          aprovador: textoDe(a.aprovador),
          divisao: textoDe(mapaDivisao[idDe(a.aprovador)] || ""),
          decisao: a.decisao || "",
          dataDecisao: toIsoDay(a.dataDecisao),
          registro: a.status || ""
        });
      });
    });

    return { linhas, aprovadores: aprovadoresLinhas, modoLimitado };
  }
};
